import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["SUPER_ADMIN", "ADMIN", "STAFF", "DRIVER"];

export default function UsersPage() {
  const { hasPerm } = useAuth();
  const canManage = hasPerm("users:manage");
  const [users, setUsers] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ role: "STAFF", status: "ACTIVE" });
  const [permUser, setPermUser] = useState(null);
  const [permData, setPermData] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/users"); setUsers(data); } catch (e) { toast.error(apiError(e)); setUsers([]); }
  }, []);
  useEffect(() => { load(); api.get("/permissions/catalog").then((r) => setCatalog(r.data.all_permissions)).catch(() => {}); }, [load]);

  const openCreate = () => { setEditing(null); setForm({ role: "STAFF", status: "ACTIVE" }); setDialog(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...u, password: "" }); setDialog(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/users/${editing.id}`, form);
      else await api.post("/users", form);
      toast.success("Saved"); setDialog(false); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const openPerms = async (u) => {
    setPermUser(u);
    const { data } = await api.get(`/users/${u.id}/permissions`);
    setPermData(data);
  };
  const togglePerm = (code, list) => {
    const set = new Set(permData[list]);
    set.has(code) ? set.delete(code) : set.add(code);
    setPermData({ ...permData, [list]: [...set] });
  };
  const savePerms = async () => {
    try {
      await api.put(`/users/${permUser.id}/permissions`, { granted: permData.granted, revoked: permData.revoked });
      toast.success("Permissions updated"); setPermUser(null);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="users-page">
      <PageHeader title="Users & Access" subtitle="Manage team members, roles and granular permissions."
        actions={canManage && <Button onClick={openCreate} className="rounded-md" data-testid="user-add-btn"><Plus className="w-4 h-4 mr-1.5" /> Add User</Button>} />

      <div className="border border-border rounded-lg bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wider">Name</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Email</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Role</TableHead>
                <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                {canManage && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {users === null ? <TableRow><TableCell colSpan={5} className="text-center py-12"><Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" /></TableCell></TableRow>
                : users.map((u) => (
                  <TableRow key={u.id} data-testid="user-row">
                    <TableCell className="py-2.5 font-medium">{u.name}</TableCell>
                    <TableCell className="py-2.5 text-muted-foreground">{u.email}</TableCell>
                    <TableCell className="py-2.5"><StatusBadge value={u.role} /></TableCell>
                    <TableCell className="py-2.5"><StatusBadge value={u.status} /></TableCell>
                    {canManage && (
                      <TableCell className="text-right py-2">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openPerms(u)} data-testid="user-perms"><ShieldCheck className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(u)} data-testid="user-edit"><Pencil className="w-4 h-4" /></Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Create/edit */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} User</DialogTitle></DialogHeader>
          <div className="space-y-3.5">
            <div className="space-y-1.5"><Label>Name</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input value={form.email || ""} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="user-status"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="ACTIVE">ACTIVE</SelectItem><SelectItem value="DISABLED">DISABLED</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>{editing ? "New Password (optional)" : "Password"}</Label>
              <Input type="password" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" placeholder={editing ? "Leave blank to keep" : "Welcome@123"} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="user-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permissions */}
      <Dialog open={!!permUser} onOpenChange={(o) => !o && setPermUser(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Permissions · {permUser?.name}</DialogTitle></DialogHeader>
          {permData && (
            <div>
              <p className="text-sm text-muted-foreground mb-3">Role <StatusBadge value={permData.role} /> grants baseline permissions. Add extra grants or revoke specific ones below.</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {catalog.map((code) => {
                  const inRole = permData.role_permissions.includes(code);
                  const granted = permData.granted.includes(code);
                  const revoked = permData.revoked.includes(code);
                  const effective = (inRole || granted) && !revoked;
                  return (
                    <label key={code} className="flex items-center gap-2 text-sm border border-border rounded-md px-3 py-2 cursor-pointer hover:bg-accent" data-testid={`perm-${code}`}>
                      <Checkbox checked={effective} onCheckedChange={() => { inRole ? togglePerm(code, "revoked") : togglePerm(code, "granted"); }} />
                      <span className={effective ? "text-foreground" : "text-muted-foreground"}>{code}</span>
                      {inRole && <span className="ml-auto text-[10px] uppercase text-muted-foreground">role</span>}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermUser(null)}>Cancel</Button>
            <Button onClick={savePerms} data-testid="perms-save">Save Permissions</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
